import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import moment from "moment";
import { useCreateOrderMutation } from "../../../../../../../../API/authApi";
import { useToastify } from "../../../../../../../../Hooks/hooks";
import useDialogHooks from "../../../../../../../../Components/Dialog/dialog-hooks";
import { IOrderDeliveryForm } from "../../../../../../../../Interfaces/formValue-interfaces";
import { INewOrder } from "../../../../../../../../Interfaces/request-interfaces";
import { IDeliveryOrderData, IOrderParams, OrderTypes } from "../../../../../../../../Interfaces/componentTypes";
import { IDeliveryOrderProps } from "./orderDeliveryDialog";
import useLanguagesHooks from "../../../../../../../../Components/Languages/setLanguage-hooks";
import SmsCodeDialog from "../SMSCode/smsCodeDialog";
import OrderSuccess from "../OrderSuccess/orderSuccess";

const useOrderDelivery = (props: IDeliveryOrderProps) => {
    const { companyId, company_name, company_logo, company_contacts, cart, deliveryMinCost, packageFee, totalPrice, setDialog, onCloseDialog, setMainCart, totalItemCount, setContentType } = props;
    const [sendOrder, { isLoading: newOrderLoading }] = useCreateOrderMutation()
    const user_id = localStorage.getItem('user_id');
    const phone = localStorage.getItem('phone');
    const totalDeliveryCost = (+deliveryMinCost) + (packageFee ?? 0) + (+totalPrice);
    const { open, onOpen: onOpenNoteDialog, onClose: onCloseNoteDialog } = useDialogHooks();
    const currentDate = new Date();
    const dialogRef = useRef<HTMLDivElement>(null);
    const { t } = useLanguagesHooks();
    const { showToastError } = useToastify();

    const {
        register,
        watch,
        control,
        formState: { errors },
        getValues,
        setValue,
        setError,
    } = useForm<IOrderDeliveryForm>({
        mode: 'onChange',
        defaultValues: {
            address: "",
            phone_number: phone ?? "",
            date: moment().format('YYYY-MM-DD'),
            time: undefined,
            notes: "",
            cash: true,
            card: false,
        },
    });

    const handleNote = (note: string) => {
        setValue('notes', note.trim() || undefined);
        onCloseNoteDialog();
    };

    useEffect(() => {
        const subscription = watch((values, { name }) => {
            if (name === "cash") {
                if (values.cash) {
                    setValue("card", false);
                } else if (!values.cash && !values.card) {
                    setValue("card", true);
                }
            }

            if (name === "card") {
                if (values.card) {
                    setValue("cash", false);
                } else if (!values.card && !values.cash) {
                    setValue("cash", true);
                }
            }
        });

        return () => subscription.unsubscribe();
    }, [watch, setValue]);

    const onOrder = async () => {
        const address = getValues('address');
        const phoneNumber = getValues('phone_number').trim().replace(/\s+/g, '');

        if (address.length < 5) {
            setError("address", { message: "" });
            dialogRef.current?.scrollTo({ top: 0, behavior: "smooth" });
            return;
        } else if (phoneNumber.length < 12) {
            setError("phone_number", { message: "" });
            dialogRef.current?.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }

        const data: IDeliveryOrderData = {
            cart: cart,
            delivery_cost: deliveryMinCost,
            packageFee: packageFee,
            total_cost: totalDeliveryCost,
        };

        const params: IOrderParams = {
            phone_number: phoneNumber,
            notes: getValues('notes'),
            address: address,
            date: getValues('date'),
            time: getValues('time'),
            card: getValues('card'),
            cash: getValues('cash'),
            totalItems: totalItemCount,
            company_id: companyId,
            company_name: company_name,
            company_logo: company_logo,
            company_contacts: company_contacts
        };

        const payload: INewOrder = {
            company_id: companyId,
            order_type: OrderTypes.DELIVERY,
            phone_number: phoneNumber,
            uuid: user_id,
            data: JSON.stringify(data),
            order_parameters: JSON.stringify(params)
        };
        try {
            const response = await sendOrder(payload).unwrap();
            setMainCart([]);
            setDialog(
                <OrderSuccess
                    setContentType={setContentType}
                    onClose={onCloseDialog}
                />)
        } catch (error: any) {
            const status = error?.error;
            const message = error?.result?.message || t('Toast.Error.Common');
            if (status === 419) {
                setDialog(
                    <SmsCodeDialog
                        data={payload}
                        setDialog={setDialog}
                        setMainCart={setMainCart}
                        onCloseDialog={onCloseDialog}
                        setContentType={setContentType}
                    />
                )
            } else {
                showToastError(t('Toast.Error.Common'));
            }
        }
    };



    return {
        open,
        errors,
        control,
        dialogRef,
        currentDate,
        newOrderLoading,
        totalDeliveryCost,
        onOrder,
        register,
        getValues,
        watch,
        handleNote,
        onOpenNoteDialog,
        onCloseNoteDialog,
    }
};

export default useOrderDelivery